// Validation helpers for wizard steps - now using centralized validation
import { validateProjectEditForm } from "../config/projectEdit.validation";
import { validateProjectObjectiveEditForm } from "../config/projectObjectiveEdit.validation";
import { validateProjectQualStepEditForm } from "../config/projectQualStepEdit.validation";

export function isValidEmail(value) {
  return /.+@.+\..+/.test(String(value || "").trim());
}

// Returns { isValid: boolean, errors: array of error messages }
export function validateStep1(metadata) {
  const validationPayload = {
    metadata: {
      name: metadata?.name,
      shortName: metadata?.shortName,
      appenPartner: metadata?.appenPartner,
      account: metadata?.account,
      hireStartDate: metadata?.hireStartDate,
      predictedCloseDate: metadata?.predictedCloseDate,
      projectStatus: metadata?.projectStatus,
      projectType: metadata?.projectType,
      projectPriority: metadata?.projectPriority,
      projectPaymentMethod: metadata?.projectPaymentMethod,
      shortDescription: metadata?.shortDescription,
    },
    objectives: {
      contributorFacingName:
        metadata?.contributorProjectName || metadata?.contributorFacingName,
    },
  };

  const validationErrors = validateProjectEditForm(validationPayload);

  // Convert to user-friendly messages
  const errors = [];
  Object.entries(validationErrors).forEach(([path, message]) => {
    const fieldNameMap = {
      "metadata.name": "Project Name",
      "metadata.shortName": "Short Project Name",
      "metadata.appenPartner": "Appen Partner",
      "metadata.account": "Account",
      "metadata.hireStartDate": "Hire Start Date",
      "metadata.predictedCloseDate": "Predicted Close Date",
      "metadata.projectStatus": "Project Status",
      "metadata.projectType": "Project Type",
      "metadata.projectPriority": "Project Priority",
      "metadata.projectPaymentMethod": "Project Payment Method",
      "metadata.shortDescription": "Short Description",
      "objectives.contributorFacingName": "Contributor Project Name",
    };
    const fieldLabel = fieldNameMap[path] || path;
    errors.push(`${fieldLabel}: ${message}`);
  });

  // Also check required fields not in centralized validation
  if (
    !metadata?.projectPaymentMethod ||
    String(metadata.projectPaymentMethod).trim() === ""
  ) {
    errors.push("Project Payment Method: This field is required");
  }
  if (!metadata?.shortDescription || String(metadata.shortDescription).trim() === "") {
    errors.push("Short Description: This field is required");
  }

  return { isValid: errors.length === 0, errors };
}

export function validateStep2(objectives) {
  // Must be an array with at least one objective
  if (!Array.isArray(objectives) || objectives.length === 0) {
    return {
      isValid: false,
      errors: ["Please add at least 1 Project Objective"],
    };
  }

  // Validate each objective has required fields
  const errors = [];
  objectives.forEach((obj, index) => {
    // Debug: Log each objective being validated with full object
    console.log(`🔍 Validating Objective ${index + 1}:`, {
      fullObjective: obj,
      objectiveName: obj.objectiveName,
      workType: obj.workType,
      workTypeType: typeof obj.workType,
      workTypeIsEmpty:
        obj.workType === undefined ||
        obj.workType === null ||
        obj.workType === "" ||
        (typeof obj.workType === "string" && obj.workType.trim() === ""),
      productivityTargetType: obj.productivityTargetType,
      daysBetweenReminderEmails: obj.daysBetweenReminderEmails,
    });

    // FIRST: Direct check for workType - if missing, add error immediately
    const workType = obj.workType;
    const workTypeIsEmpty =
      workType === undefined ||
      workType === null ||
      workType === "" ||
      (typeof workType === "string" && workType.trim() === "");

    if (workTypeIsEmpty) {
      const objectiveLabel =
        objectives.length > 1 ? `Objective ${index + 1}` : "Objective";
      errors.push(`${objectiveLabel}: Work Type: This field is required`);
      console.log(`❌ Objective ${index + 1} missing workType - added error`);
    }

    // Ensure workType exists - if not, set to empty string for validation
    // Handle cases where workType might be undefined, null, or missing entirely
    const workTypeValue =
      obj.workType !== undefined && obj.workType !== null
        ? String(obj.workType).trim()
        : "";

    // Also check other required fields similarly
    const productivityTargetTypeValue =
      obj.productivityTargetType !== undefined &&
      obj.productivityTargetType !== null
        ? String(obj.productivityTargetType).trim()
        : "";

    const daysBetweenReminderEmailsValue =
      obj.daysBetweenReminderEmails !== undefined &&
      obj.daysBetweenReminderEmails !== null
        ? String(obj.daysBetweenReminderEmails).trim()
        : "";

    // Convert objective to validation format
    const countryValue =
      obj.country !== undefined && obj.country !== null
        ? String(obj.country).trim()
        : "";
    const languageValue =
      obj.language !== undefined && obj.language !== null
        ? String(obj.language).trim()
        : "";

    const validationPayload = {
      info: {
        name: obj.objectiveName || "",
        contributorFacingName: obj.contributorFacingName || "",
        dateStart: obj.dateStart || "",
        dateEnd: obj.dateEnd || "",
        // Wizard may not have a real SF id yet; use stored projectId or placeholder
        projectId: obj.projectId || obj.project || "TEMP",
      },
      requirements: {
        workType: workTypeValue,
        country: countryValue,
        language: languageValue,
      },
      diversity: {
        minimumContributors: obj.minimumContributors,
      },
      productivity: {
        productivityTargetType: productivityTargetTypeValue,
      },
      targets: {
        productionPaymentThreshold: obj.productionPaymentThreshold,
        qualityPaymentThreshold: obj.qualityPaymentThreshold,
      },
      actionRules: {
        daysBetweenReminderEmails: daysBetweenReminderEmailsValue,
      },
      funnelSplits: {
        funnelA: obj.funnelAPercentage,
        funnelB: obj.funnelBPercentage,
        funnelC: obj.funnelCPercentage,
        funnelD: obj.funnelDPercentage,
        funnelE: obj.funnelEPercentage,
      },
    };

    console.log(
      `📦 Validation payload for Objective ${index + 1}:`,
      validationPayload,
    );
    const validationErrors = validateProjectObjectiveEditForm(
      validationPayload,
      true,
    );
    console.log(
      `📋 Validation errors for Objective ${index + 1}:`,
      validationErrors,
    );

    // Convert validation errors to user-friendly messages
    Object.entries(validationErrors).forEach(([path, message]) => {
      const objectiveLabel =
        objectives.length > 1 ? `Objective ${index + 1}` : "Objective";
      // Only add if not already added (to avoid duplicate workType errors)
      const errorMessage = `${objectiveLabel}: ${message}`;
      if (!errors.includes(errorMessage)) {
        errors.push(errorMessage);
      }
    });
  });

  const result = { isValid: errors.length === 0, errors };
  console.log("🎯 Final Step 2 validation result:", result);
  return result;
}

export function validateStep3(qualificationSteps, metadata = {}) {
  // Require at least one qualification step
  if (!Array.isArray(qualificationSteps) || qualificationSteps.length === 0) {
    return {
      isValid: false,
      errors: ["Please add at least 1 Qualification Step"],
    };
  }

  // Validate each qualification step has required fields
  const errors = [];
  qualificationSteps.forEach((step, index) => {
    // Convert step to validation format
    // Use qualificationStepId if available (from searchable field), otherwise qualificationStep
    const qualificationStepId =
      step.qualificationStepId || step.qualificationStep;
    const projectId = step.projectId || step.project || metadata?.id;

    const validationPayload = {
      info: {
        projectId: projectId,
        qualificationStepId: qualificationStepId,
        funnel: step.funnel,
        stepNumber: step.stepNumber,
        numberOfAttempts: step.numberOfAttempts,
        estimatedTimeMinutes: step.estimatedTime,
      },
    };

    const validationErrors = validateProjectQualStepEditForm(validationPayload);

    // Convert validation errors to user-friendly messages
    Object.entries(validationErrors).forEach(([path, message]) => {
      const stepLabel =
        qualificationSteps.length > 1
          ? `Qualification Step ${index + 1}`
          : "Qualification Step";
      errors.push(`${stepLabel}: ${message}`);
    });
  });

  return { isValid: errors.length === 0, errors };
}

export function validateStep4(pages) {
  // Require at least one project page
  if (!Array.isArray(pages) || pages.length === 0) {
    return { isValid: false, errors: ["Please add at least 1 Project Page"] };
  }
  return { isValid: true, errors: [] };
}
